import styled from 'styled-components'
import { Model } from '../../../../types/model'
import {
  Text,
  PrimaryAction,
  gaps,
  paddings,
  SupportingText,
  B,
  AlertError,
  AlertWarning,
  InputTextArea,
  TertiaryAction,
} from '@ohgoodlord/design-system'
import { FormEvent, useState } from 'react'
import MarkdownIt from 'markdown-it'
import { OverlayComponentProps } from '../../types'
import { FileUploadCard } from '../../../file-upload-card'
import { IFileOption, getFileGroup } from '../../../../../components/file/utils'
import { FileSummary } from './file-summary'
import { DefaultFooter } from '../default-footer'
import { genericDocumentUploadError, uploadFiles } from '../../../../utils'
import { logErrorToDatadog, track } from '../../../../../common/utils'

const md = new MarkdownIt({ html: false, linkify: false })

export const MULTI_FILE_UPLOAD = 'multi-file-upload' as const

export interface RequirementsChecklist {
  title: string
  items: string[]
}

export interface MultiFileUploadOverlayModel extends Model<typeof MULTI_FILE_UPLOAD> {
  overlayTitle: string
  fileOptions: IFileOption[]
  fileDescription?: string
  requirementsChecklist?: RequirementsChecklist
  confirmationText?: string
  hasAdditionalContext?: boolean
  accept?: string
  fileTypeHelperText?: string
}

const StyledContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${gaps.m};
  padding-bottom: ${paddings.l};
  overflow-y: scroll;
`

const StyledDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.s};
  justify-content: center;
  align-items: flex-start;
  padding: 0 20px;
`

const FileCountWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`

const FileSummaryWrapper = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;
  row-gap: ${gaps.s};
`

const StyledFooter = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 4px;
`

const ConfirmationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 12px;
  align-items: center;
  cursor: pointer;
`

const CheckboxWrapper = styled.div`
  margin-right: 8px;
  color: grey;
  display: flex;
  flex-direction: row;
  align-items: center;
  > input {
    height: 20px;
    width: 20px;
  }
`

const DisabledFooterOverlay = styled.div`
  background-color: black;
  position: absolute;
  opacity: 0.05;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
`

const StyledAdditionalDetailsWrapper = styled.div`
  position: relative;
  padding: 0 20px;
  .input-control-group .control-area textarea {
    min-height: 150px;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  position: absolute;
  top: -12%;
  left: 5%;
  background-color: white;
  z-index: 9999;
  transition: all 0.5s;
`

const Wrapper = styled.div`
  height: 100%;
  overflow-y: hidden;
`

const MarkdownContent = styled.div`
  font-size: 16px;
  color: #082842;

  p {
    margin: 0 0 4px;
  }

  ul {
    margin: 0;
    padding: 0 0 0 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  li {
    margin: 0;
  }
`

const pdfMimeType = 'application/pdf'
export const nonPdfWarningCopy =
  'Uploading a non-PDF document may delay your application'

// ponytail: only supports **bold** markdown, which is all requirementsChecklist items use.
// Swap for a markdown-to-jsx renderer if items need more formatting later.
const renderBoldText = (text: string) =>
  text
    .split(/\*\*(.+?)\*\*/g)
    .map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part))

export type Props = OverlayComponentProps<MultiFileUploadOverlayModel>

export const MultiFileUploadOverlay = ({
  model,
  container,
  sas,
  uri,
  renderOverlayHeader,
  onOverlaySubmit,
  onSendEvent,
  onSendMessage,
  isMobile,
}: Props) => {
  const [isConfirmationChecked, setIsConfirmationChecked] = useState<boolean>(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [fileUploadError, setFileUploadError] = useState<string | null>(null)
  const [additionalDetails, setAdditionalDetails] = useState<string>('')
  const [additionalContextExpanded, setAdditionalContextExpanded] =
    useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const fileName = getFileGroup(model.fileOptions[0])
  const hasUploadedFiles = selectedFiles.length > 0
  const hasNonPdfFile = selectedFiles.some(file => file.type !== pdfMimeType)
  const isPdfOnly = model.accept === 'application/pdf'

  return (
    <Wrapper>
      <StyledContainer>
        {renderOverlayHeader !== undefined && renderOverlayHeader()}
        <StyledDescription>
          {model.requirementsChecklist && model.requirementsChecklist.items.length > 0 && (
            <MarkdownContent>
              <Text align="left">{model.requirementsChecklist.title}</Text>
              <ul>
                {model.requirementsChecklist.items.map((item, index) => (
                  <li key={index}>{renderBoldText(item)}</li>
                ))}
              </ul>
            </MarkdownContent>
          )}

          {model.fileDescription && (
            isPdfOnly
              ? <MarkdownContent dangerouslySetInnerHTML={{ __html: md.render(model.fileDescription) }} />
              : <Text align="left">{model.fileDescription}</Text>
          )}

          <FileCountWrapper>
            <Text>
              <B>{selectedFiles.length} files uploaded</B>
            </Text>
          </FileCountWrapper>

          <FileUploadCard
            setFiles={files => {
              track('Click - Selected file(s)', {})
              setSelectedFiles(prevFiles => [...prevFiles, ...files])
            }}
            isMobile={isMobile}
            fileName={fileName}
            isPdf={isPdfOnly}
            fileTypeHelperText={model.fileTypeHelperText}
          />

          {hasNonPdfFile && (
            <AlertWarning>
              <Text>{nonPdfWarningCopy}</Text>
            </AlertWarning>
          )}
        </StyledDescription>
        {model.hasAdditionalContext && (
          <>
            <StyledAdditionalDetailsWrapper>
              <TitleWrapper onClick={() => setAdditionalContextExpanded(true)}>
                <>
                  {additionalContextExpanded ? (
                    <SupportingText>Supporting details (optional)</SupportingText>
                  ) : (
                    <TertiaryAction>Add supporting details (optional)</TertiaryAction>
                  )}
                </>
              </TitleWrapper>
              {additionalContextExpanded && (
                <InputTextArea
                  value={additionalDetails}
                  onChange={(e: FormEvent<HTMLTextAreaElement>) => {
                    setAdditionalDetails(e.currentTarget.value)
                  }}
                  name="additionalDetails"
                  id="additionalDetails"
                ></InputTextArea>
              )}
            </StyledAdditionalDetailsWrapper>
          </>
        )}
        <FileSummaryWrapper>
          <FileSummary
            handleRemoveFile={fileIndexToRemove => {
              track('Click - Remove selected file', {})

              setSelectedFiles(prevFiles =>
                prevFiles.filter((_, index) => index !== fileIndexToRemove),
              )
            }}
            files={selectedFiles}
          />
        </FileSummaryWrapper>
      </StyledContainer>
      <DefaultFooter
        renderActions={() => (
          <>
            {!hasUploadedFiles && <DisabledFooterOverlay />}
            <StyledFooter>
              {fileUploadError && (
                <AlertError>
                  <Text>{fileUploadError}</Text>
                </AlertError>
              )}
              {model.confirmationText !== undefined && (
                <ConfirmationWrapper>
                  <CheckboxWrapper>
                    <input
                      disabled={!hasUploadedFiles}
                      data-testid="confirmation-checkbox"
                      type="checkbox"
                      onChange={() => {
                        track('Click - Confirmation checkbox', {})
                        setIsConfirmationChecked(!isConfirmationChecked)
                      }}
                      checked={isConfirmationChecked}
                    />
                  </CheckboxWrapper>
                  <div
                    onClick={() => {
                      if (!hasUploadedFiles) {
                        return
                      }
                      track('Click - Confirmation checkbox', {})
                      setIsConfirmationChecked(!isConfirmationChecked)
                    }}
                  >
                    <SupportingText>{model.confirmationText}</SupportingText>
                  </div>
                </ConfirmationWrapper>
              )}
              <PrimaryAction
                testId="modal-primary-action-button"
                disabled={
                  !hasUploadedFiles ||
                  (!isConfirmationChecked && model.confirmationText !== undefined)
                }
                loading={isUploading}
                isFullWidth
                onClick={async () => {
                  setIsUploading(true)
                  track('Click - Uploading files', {})
                  uploadFiles(selectedFiles, container, sas, uri, 'multi_file_upload')
                    .then(fileData => {
                      const value = model.hasAdditionalContext
                        ? { files: fileData, additionalDetails }
                        : fileData

                      onSendEvent({
                        name: 'form',
                        value,
                      })
                      onSendMessage({
                        message: `Attached ${
                          selectedFiles.length > 1
                            ? `${selectedFiles.length} files`
                            : '1 file'
                        }`,
                      })
                      onOverlaySubmit()
                    })
                    .catch(e => {
                      logErrorToDatadog('Multi file upload overlay error', {
                        message: e.message ?? '',
                        error: e,
                      })
                      setFileUploadError(
                        e.message && e.message !== ''
                          ? e.message
                          : genericDocumentUploadError,
                      )
                    })
                    .finally(() => {
                      setIsUploading(false)
                    })
                }}
              >
                Done
              </PrimaryAction>
            </StyledFooter>
          </>
        )}
      />
    </Wrapper>
  )
}
